/**
 * Import completed agent runs into the Agent Review queue.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { type SelectOption } from "@/components/ui/select-dropdown";
import { SearchableTaskTypeDropdown } from "./SearchableTaskTypeDropdown";
import RadioGroup, { type RadioOption } from "@/components/ui/radio-group";
import FormInput from "@/components/ui/form-input";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  agentReviewAPI,
  type ImportCandidate,
  type ChatImportCandidate,
  type ImportLookupCandidate,
  type ImportLookupResult,
  type ImportResult,
} from "@/services/api/agentReview";
import { INTEGRATIONS_SELECT_BUTTON_CLASS } from "@/components/ops/triggers/email-triggers/integrationSelectStyles";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { SegmentedTabs } from "@/components/ui/segmented-tabs/SegmentedTabs";

type ImportMode = "specific" | "random" | "by_id" | "chats";
type ImportListView = "available" | "in_queue";
type ChatImportTab = "recent" | "search" | "practice";

const CHAT_CATALOGUE_KEY = "worker_chat";
const CHAT_CANDIDATE_LIMIT = 50;

const MODE_OPTIONS: RadioOption<ImportMode>[] = [
  {
    value: "specific",
    label: "Specific task selection",
    description: "Pick completed runs to review as live cases.",
  },
  {
    value: "by_id",
    label: "Search by task or run id",
    description: "Look up a historical agentic task or interaction id.",
  },
  {
    value: "random",
    label: "Random sample of tasks",
    description: "Generate practice samples with injected task-failing faults (static mutations).",
  },
  {
    value: "chats",
    label: "Recent chats",
    description: "Pick personal-worker conversations to review end-to-end.",
  },
];

const BLOCK_REASON_LABELS: Record<string, string> = {
  not_completed: "Run is not completed",
  not_rateable: "Run has no rateable agent steps",
  wrong_tenant: "Run belongs to another tenant",
  already_queued: "Already in the review queue",
};

function blockReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "Cannot import this run";
  return BLOCK_REASON_LABELS[reason] ?? reason.replace(/_/g, " ");
}

function CandidateRow({
  candidate,
  checked,
  disabled,
  onToggle,
  showIds = false,
}: {
  candidate: ImportCandidate | ImportLookupCandidate;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  showIds?: boolean;
}) {
  const lookup = "importable" in candidate ? candidate : null;
  const title =
    candidate.subject_label?.trim() ||
    candidate.interaction_id.slice(-8);
  const metaParts = [
    formatRelativeTime(candidate.completed_at),
    candidate.step_count > 0 ? `${candidate.step_count} steps` : null,
    candidate.disposition ? candidate.disposition.replace(/_/g, " ") : null,
    lookup?.interaction_status ? `status: ${lookup.interaction_status}` : null,
    candidate.client_id,
  ].filter(Boolean);

  return (
    <label
      className={clsx(
        "flex items-start gap-3 rounded-lg px-3 py-2 cursor-pointer",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-700/40",
      )}
    >
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-200 font-medium truncate">{title}</span>
        {candidate.summary_snippet ? (
          <span className="block text-xs text-slate-400 mt-0.5 line-clamp-2">
            {candidate.summary_snippet}
          </span>
        ) : null}
        <span className="block text-xs text-slate-500 mt-1">{metaParts.join(" · ")}</span>
        {lookup && !lookup.importable && lookup.block_reason ? (
          <span className="block text-xs text-amber-400/90 mt-1">
            {blockReasonLabel(lookup.block_reason)}
          </span>
        ) : null}
        {showIds ? (
          <span className="block text-[10px] text-slate-600 mt-0.5 font-mono truncate">
            task {candidate.task_id || "—"} · run {candidate.interaction_id}
          </span>
        ) : candidate.subject_label ? (
          <span className="block text-[10px] text-slate-600 mt-0.5 font-mono truncate">
            {candidate.interaction_id}
          </span>
        ) : null}
      </span>
    </label>
  );
}

function ChatCandidateRow({
  candidate,
  checked,
  disabled,
  onToggle,
}: {
  candidate: ChatImportCandidate;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const userName = candidate.user_name?.trim() || "";
  const topic = candidate.subject_label?.trim() || "";
  const title = userName || topic || candidate.conversation_id.slice(-8);
  const metaParts = [
    formatRelativeTime(candidate.completed_at),
    candidate.message_count > 0 ? `${candidate.message_count} messages` : null,
    candidate.interaction_count > 0
      ? `${candidate.interaction_count} turn${candidate.interaction_count === 1 ? "" : "s"}`
      : null,
    candidate.agent_name ? `Worker: ${candidate.agent_name}` : null,
  ].filter(Boolean);

  return (
    <label
      className={clsx(
        "flex items-start gap-3 rounded-lg px-3 py-2 cursor-pointer",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-700/40",
      )}
    >
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-200 font-medium truncate">
          {userName ? (
            <>
              <span className="text-slate-400 font-normal">User </span>
              {userName}
            </>
          ) : (
            title
          )}
        </span>
        {userName && topic ? (
          <span className="block text-xs text-slate-300 mt-0.5 truncate">{topic}</span>
        ) : null}
        {candidate.summary_snippet ? (
          <span className="block text-xs text-slate-400 mt-0.5 line-clamp-2">
            {candidate.summary_snippet}
          </span>
        ) : null}
        <span className="block text-xs text-slate-500 mt-1">{metaParts.join(" · ")}</span>
        <span className="block text-[10px] text-slate-600 mt-0.5 font-mono truncate">
          {candidate.conversation_id}
        </span>
      </span>
    </label>
  );
}

export interface ImportTasksDialogProps {
  open: boolean;
  onClose: () => void;
  defaultCatalogueKey?: string | null;
  sessionId?: string | null;
  onImported: (result: ImportResult, catalogueKey: string) => void;
}

export function ImportTasksDialog({
  open,
  onClose,
  defaultCatalogueKey,
  sessionId,
  onImported,
}: ImportTasksDialogProps) {
  const [mode, setMode] = useState<ImportMode>("specific");
  const [catalogueKey, setCatalogueKey] = useState<string>("");
  const [catalogueOptions, setCatalogueOptions] = useState<SelectOption<string>[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);

  const [candidates, setCandidates] = useState<ImportCandidate[]>([]);
  const [chatCandidates, setChatCandidates] = useState<ChatImportCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [chatCandidatesLoading, setChatCandidatesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [searchQuery, setSearchQuery] = useState("");
  const [lookupResult, setLookupResult] = useState<ImportLookupResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [sampleCount, setSampleCount] = useState("10");
  const [chatSampleCount, setChatSampleCount] = useState("10");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [listView, setListView] = useState<ImportListView>("available");
  const [chatTab, setChatTab] = useState<ChatImportTab>("recent");
  const [chatUserQuery, setChatUserQuery] = useState("");
  const [chatSearchApplied, setChatSearchApplied] = useState("");

  const importSessionId = useMemo(() => {
    if (mode === "chats") {
      return defaultCatalogueKey === CHAT_CATALOGUE_KEY ? (sessionId ?? null) : null;
    }
    return defaultCatalogueKey && catalogueKey && catalogueKey === defaultCatalogueKey
      ? (sessionId ?? null)
      : null;
  }, [defaultCatalogueKey, catalogueKey, sessionId, mode]);

  useEffect(() => {
    if (!open) return;
    setMode("specific");
    setSelectedIds(new Set());
    setError(null);
    setResultMessage(null);
    setListView("available");
    setChatTab("recent");
    setChatUserQuery("");
    setChatSearchApplied("");
    setSampleCount("10");
    setChatSampleCount("10");
    setSearchQuery("");
    setLookupResult(null);
    setChatCandidates([]);
    setCatalogueLoading(true);
    void agentReviewAPI.listImportCatalogueOptions()
      .then((items) => {
        const options = items.map((item) => ({
          value: item.catalogue_key,
          label: item.display_name || item.catalogue_key,
        }));
        setCatalogueOptions(options);
        const preferred = defaultCatalogueKey && options.some((o) => o.value === defaultCatalogueKey)
          ? defaultCatalogueKey
          : options[0]?.value ?? "";
        setCatalogueKey(preferred);
      })
      .catch(() => setError("Couldn't load task types."))
      .finally(() => setCatalogueLoading(false));
  }, [open, defaultCatalogueKey]);

  const loadCandidates = useCallback(async () => {
    if (!catalogueKey || mode !== "specific") return;
    setCandidatesLoading(true);
    setError(null);
    try {
      const rows = await agentReviewAPI.listImportCandidates(catalogueKey);
      setCandidates(rows);
      setSelectedIds(new Set(rows.filter((r) => !r.already_in_queue).map((r) => r.interaction_id)));
    } catch {
      setError("Couldn't load completed runs for this task type.");
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, [catalogueKey, mode]);

  const loadChatCandidates = useCallback(async (userQuery?: string | null) => {
    if (mode !== "chats") return;
    setChatCandidatesLoading(true);
    setError(null);
    try {
      const rows = await agentReviewAPI.listChatImportCandidates(
        CHAT_CANDIDATE_LIMIT,
        userQuery ?? null,
      );
      setChatCandidates(rows);
      setSelectedIds(new Set(rows.filter((r) => !r.already_in_queue).map((r) => r.conversation_id)));
    } catch {
      setError(
        userQuery?.trim()
          ? "Couldn't search worker chats for that user."
          : "Couldn't load recent worker chats.",
      );
      setChatCandidates([]);
    } finally {
      setChatCandidatesLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    if (open && mode === "specific" && catalogueKey) {
      void loadCandidates();
    }
  }, [open, mode, catalogueKey, loadCandidates]);

  useEffect(() => {
    if (open && mode === "chats" && chatTab === "recent") {
      void loadChatCandidates(null);
    }
  }, [open, mode, chatTab, loadChatCandidates]);

  useEffect(() => {
    if (open && mode === "chats" && chatTab === "search" && chatSearchApplied) {
      void loadChatCandidates(chatSearchApplied);
    }
  }, [open, mode, chatTab, chatSearchApplied, loadChatCandidates]);

  const lookupCandidates = lookupResult?.candidates ?? [];

  const selectableCandidates = useMemo(
    () => candidates.filter((c) => !c.already_in_queue),
    [candidates],
  );

  const selectableChatCandidates = useMemo(
    () => chatCandidates.filter((c) => !c.already_in_queue),
    [chatCandidates],
  );

  const selectableLookupCandidates = useMemo(
    () => lookupCandidates.filter((c) => c.importable && !c.already_in_queue),
    [lookupCandidates],
  );

  const queuedCandidates = useMemo(
    () => candidates.filter((c) => c.already_in_queue),
    [candidates],
  );

  const queuedChatCandidates = useMemo(
    () => chatCandidates.filter((c) => c.already_in_queue),
    [chatCandidates],
  );

  const visibleCandidates = listView === "in_queue" ? queuedCandidates : selectableCandidates;
  const visibleChatCandidates =
    listView === "in_queue" ? queuedChatCandidates : selectableChatCandidates;

  const toggleSelection = (interactionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(interactionId)) next.delete(interactionId);
      else next.add(interactionId);
      return next;
    });
  };

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) {
      setError("Enter a task id or interaction (run) id.");
      return;
    }
    setSearchLoading(true);
    setError(null);
    setResultMessage(null);
    setLookupResult(null);
    setSelectedIds(new Set());
    try {
      const result = await agentReviewAPI.lookupImport(query);
      setLookupResult(result);
      if (result.candidates.length === 0) {
        setError(
          result.lookup_type === "task_id"
            ? "Task found but it has no agent runs yet."
            : "No matching run found.",
        );
        return;
      }
      const importable = result.candidates.filter((c) => c.importable && !c.already_in_queue);
      if (importable.length === 0) {
        setError("Found run(s) but none can be imported — see notes below.");
      }
      setSelectedIds(new Set(importable.map((c) => c.interaction_id)));
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 404) {
        setError("No task or run found for that id. Check the id and try again.");
      } else if (status === 403) {
        setError("Search failed. Admin access is required.");
      } else {
        setError("Couldn't look up that id. Try again.");
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setResultMessage(null);
    try {
      let result: ImportResult;
      let importedCatalogueKey = catalogueKey;

      if (mode === "specific") {
        if (!catalogueKey) return;
        if (selectedIds.size === 0) {
          setError("Select at least one run to import.");
          return;
        }
        result = await agentReviewAPI.importSpecific([...selectedIds], importSessionId);
      } else if (mode === "chats") {
        if (chatTab === "practice") {
          const count = Math.max(1, Math.min(50, Number.parseInt(chatSampleCount, 10) || 10));
          result = await agentReviewAPI.importChatRandomSample({
            count,
            sessionId: importSessionId,
          });
          importedCatalogueKey = CHAT_CATALOGUE_KEY;
        } else {
          if (selectedIds.size === 0) {
            setError("Select at least one chat to import.");
            return;
          }
          result = await agentReviewAPI.importChatSpecific([...selectedIds], importSessionId);
          importedCatalogueKey = CHAT_CATALOGUE_KEY;
        }
      } else if (mode === "by_id") {
        const picked = selectableLookupCandidates.filter((c) => selectedIds.has(c.interaction_id));
        if (picked.length === 0) {
          setError("Select at least one importable run.");
          return;
        }
        result = await agentReviewAPI.importSpecific(picked.map((c) => c.interaction_id), importSessionId);
        importedCatalogueKey = picked[0]?.catalogue_key ?? catalogueKey;
      } else {
        if (!catalogueKey) return;
        const count = Math.max(1, Math.min(50, Number.parseInt(sampleCount, 10) || 10));
        result = await agentReviewAPI.importRandomSample({ catalogueKey, count, sessionId: importSessionId });
      }

      const skipped = result.skipped.length;
      if (mode === "random" && result.imported_count === 0) {
        const reason = result.skipped[0]?.reason;
        setError(
          reason === "no_candidates"
            ? "No suitable completed runs found to base practice samples on. Complete a run for this task type, import a specific run, or copy test data from staging."
            : "No new practice cases were created. Try again or pick a different task type.",
        );
        return;
      }
      if (mode === "chats" && chatTab === "practice" && result.imported_count === 0) {
        const reason = result.skipped[0]?.reason;
        setError(
          reason === "no_candidates"
            ? "No suitable worker chats found to base practice samples on."
            : "No new practice chat cases were created. Try again.",
        );
        return;
      }
      if (result.imported_count === 0 && mode === "by_id") {
        setError("Import failed — the selected run may no longer be eligible.");
        return;
      }
      setResultMessage(
        `Imported ${result.imported_count} case${result.imported_count === 1 ? "" : "s"}` +
          (skipped ? ` (${skipped} skipped)` : "") +
          ".",
      );
      onImported(result, importedCatalogueKey);
      if (mode === "specific") {
        void loadCandidates();
      } else if (mode === "chats" && chatTab !== "practice") {
        void loadChatCandidates(chatTab === "search" ? chatSearchApplied : null);
      } else if (mode === "by_id" && searchQuery.trim()) {
        try {
          const refreshed = await agentReviewAPI.lookupImport(searchQuery.trim());
          setLookupResult(refreshed);
          const importable = refreshed.candidates.filter((c) => c.importable && !c.already_in_queue);
          setSelectedIds(new Set(importable.map((c) => c.interaction_id)));
        } catch {
          // Non-fatal after successful import.
        }
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 504 || status === 408) {
        setError("Import timed out. Try fewer samples or try again in a moment.");
      } else if (status === 403) {
        setError("Import failed. Admin access is required.");
      } else {
        setError("Import failed. Check you have admin access and try again.");
      }
    } finally {
      setImporting(false);
    }
  };

  const importDisabled =
    importing ||
    (mode === "specific" && candidatesLoading) ||
    (mode === "chats" && chatTab !== "practice" && chatCandidatesLoading) ||
    (mode === "specific" && (!catalogueKey || listView === "in_queue" || selectedIds.size === 0)) ||
    (mode === "chats" &&
      chatTab !== "practice" &&
      (listView === "in_queue" || selectedIds.size === 0)) ||
    (mode === "chats" && chatTab === "search" && !chatSearchApplied) ||
    (mode === "random" && !catalogueKey) ||
    (mode === "by_id" && selectedIds.size === 0);

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>{mode === "chats" ? "Import for review" : "Import tasks"}</DialogTitle>
      <DialogBody className="space-y-5">
        <RadioGroup
          name="import-mode"
          value={mode}
          onChange={(next) => {
            setMode(next);
            setSelectedIds(new Set());
            setError(null);
            setResultMessage(null);
            setListView("available");
            if (next !== "chats") {
              setChatCandidates([]);
              setChatTab("recent");
              setChatUserQuery("");
              setChatSearchApplied("");
            }
            if (next !== "by_id") {
              setLookupResult(null);
              setSearchQuery("");
            }
          }}
          options={MODE_OPTIONS}
          variant="cards"
        />

        {mode !== "by_id" && mode !== "chats" ? (
          <div className="max-w-md">
            <SearchableTaskTypeDropdown
              key={open ? "open" : "closed"}
              buttonClassName={INTEGRATIONS_SELECT_BUTTON_CLASS}
              options={catalogueOptions}
              value={catalogueKey}
              onChange={setCatalogueKey}
              disabled={catalogueLoading || catalogueOptions.length === 0}
              data-testid="agent-review-import-task-type"
            />
          </div>
        ) : null}

        {mode === "specific" ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-400">
                20 most recent completed runs for this task type.
              </p>
              {listView === "available" && selectableCandidates.length > 0 ? (
                <Button
                  plain
                  className="!text-slate-300 text-xs"
                  onClick={() =>
                    setSelectedIds(
                      selectedIds.size === selectableCandidates.length
                        ? new Set()
                        : new Set(selectableCandidates.map((c) => c.interaction_id)),
                    )
                  }
                >
                  {selectedIds.size === selectableCandidates.length ? "Clear all" : "Select all"}
                </Button>
              ) : null}
            </div>

            <SegmentedTabs
              activeId={listView}
              onChange={(id) => setListView(id as ImportListView)}
              tabs={[
                {
                  id: "available",
                  label: "Available",
                  count: selectableCandidates.length,
                  countVariant: selectableCandidates.length > 0 ? "attention" : "neutral",
                },
                {
                  id: "in_queue",
                  label: "In queue",
                  count: queuedCandidates.length,
                },
              ]}
            />

            {candidatesLoading ? (
              <div className="space-y-2">
                <LoadingPlaceholder height="48px" />
                <LoadingPlaceholder height="48px" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                No completed runs found for this task type.
              </p>
            ) : visibleCandidates.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">
                {listView === "in_queue"
                  ? "None of these recent runs are in the review queue yet."
                  : "All recent runs are already in the queue — check In queue."}
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto space-y-2 rounded-xl border border-slate-600/60 p-2">
                {visibleCandidates.map((candidate) => (
                  <li key={candidate.interaction_id}>
                    <CandidateRow
                      candidate={candidate}
                      checked={selectedIds.has(candidate.interaction_id)}
                      disabled={candidate.already_in_queue}
                      onToggle={() => toggleSelection(candidate.interaction_id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {mode === "chats" ? (
          <div className="space-y-3">
            <SegmentedTabs
              activeId={chatTab}
              onChange={(id) => {
                const next = id as ChatImportTab;
                setChatTab(next);
                setError(null);
                setResultMessage(null);
                setListView("available");
                setSelectedIds(new Set());
                if (next === "recent") {
                  setChatSearchApplied("");
                  setChatUserQuery("");
                }
                if (next === "search") {
                  setChatCandidates([]);
                }
              }}
              tabs={[
                { id: "recent", label: "Recent" },
                { id: "search", label: "Search by user" },
                { id: "practice", label: "Practice samples" },
              ]}
            />

            {chatTab === "practice" ? (
              <div className="space-y-3 max-w-xs">
                <FormInput
                  label="Number of practice chats"
                  type="number"
                  min={1}
                  max={50}
                  value={chatSampleCount}
                  onChange={(e) => setChatSampleCount(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Generates blind practice chats from real worker conversations with injected
                  faults (wrong tools, fabricated replies, ignored failures) for reviewer and Ace
                  calibration.
                </p>
              </div>
            ) : (
              <>
                {chatTab === "search" ? (
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1 min-w-0">
                      <FormInput
                        label="Adviser name or email"
                        value={chatUserQuery}
                        onChange={(e) => setChatUserQuery(e.target.value)}
                        placeholder="e.g. Alice or alice@…"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const q = chatUserQuery.trim();
                            if (!q) {
                              setError("Enter a name or email to search.");
                              return;
                            }
                            setChatSearchApplied(q);
                          }
                        }}
                      />
                    </div>
                    <Button
                      onClick={() => {
                        const q = chatUserQuery.trim();
                        if (!q) {
                          setError("Enter a name or email to search.");
                          return;
                        }
                        setError(null);
                        setChatSearchApplied(q);
                      }}
                      disabled={chatCandidatesLoading || !chatUserQuery.trim()}
                    >
                      {chatCandidatesLoading ? "Searching…" : "Search"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-400">
                      {CHAT_CANDIDATE_LIMIT} most recent personal-worker chats with at least one
                      completed turn.
                    </p>
                    {listView === "available" && selectableChatCandidates.length > 0 ? (
                      <Button
                        plain
                        className="!text-slate-300 text-xs"
                        onClick={() =>
                          setSelectedIds(
                            selectedIds.size === selectableChatCandidates.length
                              ? new Set()
                              : new Set(selectableChatCandidates.map((c) => c.conversation_id)),
                          )
                        }
                      >
                        {selectedIds.size === selectableChatCandidates.length
                          ? "Clear all"
                          : "Select all"}
                      </Button>
                    ) : null}
                  </div>
                )}

                {chatTab === "search" && chatSearchApplied && listView === "available" && selectableChatCandidates.length > 0 ? (
                  <div className="flex justify-end">
                    <Button
                      plain
                      className="!text-slate-300 text-xs"
                      onClick={() =>
                        setSelectedIds(
                          selectedIds.size === selectableChatCandidates.length
                            ? new Set()
                            : new Set(selectableChatCandidates.map((c) => c.conversation_id)),
                        )
                      }
                    >
                      {selectedIds.size === selectableChatCandidates.length
                        ? "Clear all"
                        : "Select all"}
                    </Button>
                  </div>
                ) : null}

                {(chatTab === "recent" || chatSearchApplied) && (
                  <>
                    <SegmentedTabs
                      activeId={listView}
                      onChange={(id) => setListView(id as ImportListView)}
                      tabs={[
                        {
                          id: "available",
                          label: "Available",
                          count: selectableChatCandidates.length,
                          countVariant:
                            selectableChatCandidates.length > 0 ? "attention" : "neutral",
                        },
                        {
                          id: "in_queue",
                          label: "In queue",
                          count: queuedChatCandidates.length,
                        },
                      ]}
                    />

                    {chatCandidatesLoading ? (
                      <div className="space-y-2">
                        <LoadingPlaceholder height="48px" />
                        <LoadingPlaceholder height="48px" />
                      </div>
                    ) : chatCandidates.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">
                        {chatTab === "search"
                          ? "No chats found for that user."
                          : "No recent worker chats found."}
                      </p>
                    ) : visibleChatCandidates.length === 0 ? (
                      <p className="text-sm text-slate-500 py-4 text-center">
                        {listView === "in_queue"
                          ? "None of these chats are in the review queue yet."
                          : "All matching chats are already in the queue — check In queue."}
                      </p>
                    ) : (
                      <ul className="max-h-80 overflow-y-auto space-y-2 rounded-xl border border-slate-600/60 p-2">
                        {visibleChatCandidates.map((candidate) => (
                          <li key={candidate.conversation_id}>
                            <ChatCandidateRow
                              candidate={candidate}
                              checked={selectedIds.has(candidate.conversation_id)}
                              disabled={candidate.already_in_queue}
                              onToggle={() => toggleSelection(candidate.conversation_id)}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}

                {chatTab === "search" && !chatSearchApplied ? (
                  <p className="text-sm text-slate-500 py-2">
                    Enter an adviser name or email, then search.
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {mode === "by_id" ? (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 min-w-0">
                <FormInput
                  label="Task id or run id"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="MongoDB ObjectId"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleSearch();
                    }
                  }}
                />
              </div>
              <Button onClick={() => void handleSearch()} disabled={searchLoading || !searchQuery.trim()}>
                {searchLoading ? "Searching…" : "Search"}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Accepts an agentic <span className="text-slate-400">task</span> id (lists recent runs on that
              task) or an <span className="text-slate-400">interaction</span> id (a single agent run). Only
              completed runs with rateable steps can be imported.
            </p>

            {searchLoading ? (
              <div className="space-y-2">
                <LoadingPlaceholder height="48px" />
              </div>
            ) : lookupCandidates.length > 0 ? (
              <div className="space-y-2">
                {lookupResult ? (
                  <p className="text-sm text-slate-400">
                    {lookupResult.lookup_type === "task_id"
                      ? `${lookupCandidates.length} run${lookupCandidates.length === 1 ? "" : "s"} on this task`
                      : "Matched interaction (run)"}
                  </p>
                ) : null}
                <ul className="max-h-80 overflow-y-auto space-y-2 rounded-xl border border-slate-600/60 p-2">
                  {lookupCandidates.map((candidate) => {
                    const disabled = !candidate.importable || candidate.already_in_queue;
                    return (
                      <li key={candidate.interaction_id}>
                        <CandidateRow
                          candidate={candidate}
                          checked={selectedIds.has(candidate.interaction_id)}
                          disabled={disabled}
                          onToggle={() => toggleSelection(candidate.interaction_id)}
                          showIds
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === "random" ? (
          <div className="space-y-3 max-w-xs">
            <FormInput
              label="Number of practice samples"
              type="number"
              min={1}
              max={50}
              value={sampleCount}
              onChange={(e) => setSampleCount(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              Generates new blind practice cases from random completed runs — each import
              applies a fresh fault mutation, even when reusing the same source run.
            </p>
          </div>
        ) : null}

        {error ? <p className="text-sm text-rose-400">{error}</p> : null}
        {resultMessage ? <p className="text-sm text-emerald-400">{resultMessage}</p> : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={importing}>
          Close
        </Button>
        <Button onClick={() => void handleImport()} disabled={importDisabled}>
          {importing ? "Importing…" : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
